<template>
  <vf-input
    ref="dateRef"
    v-model="maskedDate"
    :disabled
    :invalid
    :label="label || $t.birthDate"
    :props-input="{ 'aria-describedby': ariaDescribedby }"
    :required
    :variant
    @change="onChange"
  >
    <template v-if="$slots.after" #after>
      <slot name="after" />
    </template>
  </vf-input>
</template>

<script lang="ts" setup>
/**
 * This component expect the model value to be a string in the format YYYY-MM-DD
 * It will emit the value in the same format, YYYY-MM-DD
 * Locale formats are handled internally and should be specified as a prop when adding EMEA support
 */
import type { Props } from '#brands/components/vf/input/Input.vue'

const { displayFormat = 'MM/DD/YYYY' } = defineProps<
  Pick<Props, 'disabled' | 'invalid' | 'variant' | 'required'> & {
    ariaDescribedby?: string
    displayFormat?: 'MM/DD/YYYY' | 'DD/MM/YYYY'
    label?: string
  }
>()

const emit = defineEmits<{
  change: [value: string]
}>()

defineSlots<{
  default?: () => void
  after?: () => void
}>()

const model = defineModel<string>({ required: true })
const dateRef = useTemplateRef('dateRef')

const { value: maskedDate, el } = useMask('XX/XX/XXXX', { regex: /\d/ })

// Temporary solution to emit 'change' event
// 'update:modelValue' should be emitted on 'input' event
const onChange = () => {
  const formatted = dateFormat(maskedDate.value, displayFormat, 'YYYY-MM-DD')

  model.value = formatted
  emit('change', formatted)
}

function resolveNativeInputEl(): HTMLInputElement | undefined {
  const comp = dateRef.value as any
  const inputRef = comp?.inputRef
  if (!inputRef) return undefined

  const direct
    = (inputRef.el as HTMLInputElement | undefined)
      ?? (inputRef.$el as HTMLInputElement | undefined)

  if (direct && direct.tagName?.toLowerCase() === 'input') {
    return direct
  }

  const wrapper
    = (inputRef.el as HTMLElement | undefined)
      ?? (inputRef.$el as HTMLElement | undefined)
  if (!wrapper) return undefined

  return wrapper.querySelector('input') ?? undefined
}

onMounted(() => {
  const inputEl = resolveNativeInputEl()
  if (inputEl) {
    el.value = inputEl as unknown as HTMLInputElement
  }
})

watch(model, (value) => {
  // the model value should always be a string in the format YYYY-MM-DD
  // this watcher will convert the value to the displayFormat
  maskedDate.value = dateFormat(value, 'YYYY-MM-DD', displayFormat)
}, { immediate: true })
</script>
